<template>
  <section class="card">
    <div class="page-head">
      <button
        type="button"
        class="back-btn"
        title="返回报名名单"
        @click="goBack"
        @mouseenter="backBtnHover = true"
        @mouseleave="backBtnHover = false"
      >
        <img
          class="back-btn-icon"
          :src="backBtnHover ? backAboutActiveIcon : backAboutIcon"
          alt="返回"
        >
      </button>
      <h2>活动排座</h2>
    </div>
    <p class="sub-line">{{ pageTitle }}</p>

    <p class="tip">
      可排座名单与报名名单一致：会务审核通过、活动费已结清、未取消。全员分配座位后方可<strong>发布</strong>；发布后小程序可见，仍可直接改座。
    </p>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>
    <p v-if="okMsg" class="ok">{{ okMsg }}</p>

    <div v-if="plan" class="plan-bar">
      <span class="plan-bar-label">方案概况</span>
      <span
        class="pill"
        :class="plan.statusCode === 'PUBLISHED' ? 'pill-green' : 'pill-blue'"
      >
        {{ plan.statusCode === 'PUBLISHED' ? '已发布' : '编辑中' }}
      </span>
      <span class="plan-stat">可排座 <strong>{{ plan.eligibleCount }}</strong> 人</span>
      <span class="plan-stat">已分配 <strong>{{ plan.assignedCount }}</strong> 人</span>
      <span v-if="plan.holdCount != null" class="plan-stat">占座 <strong>{{ plan.holdCount }}</strong> 个</span>
      <span v-if="plan.publishedAt" class="plan-stat muted-inline">发布于 {{ plan.publishedAt }}</span>
    </div>

    <div class="toolbar-row">
      <label class="tool-inline">
        <span class="tool-label">桌号上限</span>
        <input v-model.number="maxTableNo" type="number" min="0" class="tool-input tool-input--num" placeholder="可选">
      </label>
      <label class="tool-inline">
        <span class="tool-label">每桌座位数</span>
        <input v-model.number="seatsPerTable" type="number" min="0" class="tool-input tool-input--num" placeholder="可选">
      </label>
      <button type="button" class="btn btn-edit" :disabled="!activityId || busy" @click="savePlanMeta">保存参数</button>
      <button type="button" class="btn btn-primary" :disabled="!activityId || busy || !rows.length" @click="saveAll">
        {{ busy ? '保存中…' : '保存座位' }}
      </button>
      <button
        type="button"
        class="btn btn-primary"
        :disabled="!activityId || busy || !plan || !plan.allEligibleAssigned || plan.statusCode === 'PUBLISHED'"
        @click="publishPlan"
      >
        发布排座
      </button>
      <button type="button" class="btn btn-cancel" :disabled="!activityId || busy" @click="load">刷新</button>
      <button type="button" class="btn btn-edit" :disabled="!activityId || busy" @click="pickSeatingImport">导入 Excel</button>
      <input ref="seatingImportInput" type="file" accept=".xlsx,.xls,.csv" class="hidden-file" @change="onSeatingImportFile" />
      <button type="button" class="btn btn-edit" :disabled="!activityId || busy" @click="addHoldRow">+ 占座</button>
      <button type="button" class="btn btn-primary" :disabled="!activityId || busy || !holdRows.length" @click="saveHolds">
        {{ busy ? '保存中…' : '保存占座' }}
      </button>
    </div>

    <div v-if="holdRows.length" class="table-wrap hold-wrap">
      <h3 class="sub-h">空位预留（占座）</h3>
      <table>
        <thead>
          <tr>
            <th class="col-center">桌号</th>
            <th class="col-center">座位号</th>
            <th>标签</th>
            <th>备注</th>
            <th class="col-center">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(h, idx) in holdRows" :key="h._key || idx">
            <td class="col-center"><input v-model.number="h._tableNo" type="number" min="1" class="cell-input"></td>
            <td class="col-center"><input v-model.number="h._seatNo" type="number" min="1" class="cell-input"></td>
            <td><input v-model="h._label" class="cell-input" placeholder="如 VIP预留"></td>
            <td><input v-model="h._note" class="cell-input" placeholder="可选"></td>
            <td class="col-center">
              <button type="button" class="btn-link" @click="removeHoldRow(idx, h)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th class="col-seq">序号</th>
            <th>姓名</th>
            <th>手机号</th>
            <th>单位</th>
            <th>报名者</th>
            <th class="col-center">桌号</th>
            <th class="col-center">座位号</th>
            <th class="col-center">预览</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in rows" :key="row.rowKey">
            <td class="col-seq">{{ idx + 1 }}</td>
            <td class="td-nowrap">{{ row.attendeeName || '—' }}</td>
            <td class="td-nowrap">{{ row.attendeePhone || '—' }}</td>
            <td class="td-wrap">{{ row.attendeeCompany || '—' }}</td>
            <td class="td-sub">{{ row.submitterInfo || '—' }}</td>
            <td class="col-center">
              <input v-model.number="row._tableNo" type="number" min="1" class="cell-input">
            </td>
            <td class="col-center">
              <input v-model.number="row._seatNo" type="number" min="1" class="cell-input">
            </td>
            <td class="col-center seat-preview">{{ previewLabel(row) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="!loading && !rows.length" class="muted">暂无可排座参会人（须审核通过且活动费已结清）。</p>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import {
  fetchAdminActivitySeatingHolds,
  getAdminCmsActivity,
  getAdminActivitySeatingEligibleAttendees,
  getAdminActivitySeatingPlan,
  importAdminActivitySeatingAssignments,
  publishAdminActivitySeatingPlan,
  saveAdminActivitySeatingAssignments,
  saveAdminActivitySeatingHolds,
  deleteAdminActivitySeatingHold,
  upsertAdminActivitySeatingPlan,
} from '@/api'
import backAboutIcon from '@/assets/icons/back-about.svg'
import backAboutActiveIcon from '@/assets/icons/back-about-active.svg'

export default {
  name: 'ActivitySeatingManageView',
  data() {
    return {
      activityId: null,
      activityTitle: '',
      plan: null,
      rows: [],
      holdRows: [],
      holdKeySeq: 0,
      maxTableNo: null,
      seatsPerTable: null,
      loading: false,
      busy: false,
      errorMsg: '',
      okMsg: '',
      backBtnHover: false,
      backAboutIcon,
      backAboutActiveIcon,
    }
  },
  computed: {
    pageTitle() {
      const t = (this.activityTitle || '').trim()
      return t || (this.activityId ? `活动 ${this.activityId}` : '')
    },
  },
  mounted() {
    this.syncRoute()
    this.load()
  },
  watch: {
    '$route.params.activityId'() {
      this.syncRoute()
      this.load()
    },
  },
  methods: {
    syncRoute() {
      const raw = this.$route.params.activityId
      const n = Number(raw)
      this.activityId = Number.isFinite(n) && n > 0 ? n : null
    },
    goBack() {
      if (this.activityId) {
        this.$router.push(`/content/activity-registrations/${this.activityId}`)
      } else {
        this.$router.push('/content/activities')
      }
    },
    previewLabel(row) {
      const t = Number(row._tableNo)
      const s = Number(row._seatNo)
      if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(s) || s <= 0) return '—'
      return `${t} 桌 · ${s} 号`
    },
    async load() {
      if (!this.activityId) return
      this.loading = true
      this.errorMsg = ''
      try {
        const act = await getAdminCmsActivity(this.activityId)
        this.activityTitle = act && act.title ? act.title : ''
        this.plan = await getAdminActivitySeatingPlan(this.activityId)
        if (this.plan) {
          this.maxTableNo = this.plan.maxTableNo != null ? this.plan.maxTableNo : null
          this.seatsPerTable = this.plan.seatsPerTable != null ? this.plan.seatsPerTable : null
        }
        const eligible = await getAdminActivitySeatingEligibleAttendees(this.activityId)
        this.rows = (eligible || []).map((r) => ({
          ...r,
          _tableNo: r.tableNo != null ? r.tableNo : '',
          _seatNo: r.seatNo != null ? r.seatNo : '',
        }))
        const holds = await fetchAdminActivitySeatingHolds(this.activityId)
        this.holdRows = (holds || []).map((h) => ({
          id: h.id,
          _key: `h-${h.id}`,
          _tableNo: h.tableNo,
          _seatNo: h.seatNo,
          _label: h.label || '',
          _note: h.note || '',
        }))
      } catch (e) {
        this.errorMsg = e && e.message ? e.message : '加载失败'
      } finally {
        this.loading = false
      }
    },
    async savePlanMeta() {
      if (!this.activityId) return
      this.busy = true
      this.okMsg = ''
      this.errorMsg = ''
      try {
        this.plan = await upsertAdminActivitySeatingPlan(this.activityId, {
          maxTableNo: this.maxTableNo || null,
          seatsPerTable: this.seatsPerTable || null,
        })
        this.okMsg = '方案参数已保存'
      } catch (e) {
        this.errorMsg = e && e.message ? e.message : '保存失败'
      } finally {
        this.busy = false
      }
    },
    async saveAll() {
      if (!this.activityId) return
      const items = this.rows
        .map((r) => {
          const t = Number(r._tableNo)
          const s = Number(r._seatNo)
          return {
            submissionId: r.submissionId,
            attendeeSlot: r.attendeeSlot,
            tableNo: Number.isFinite(t) && t > 0 ? t : null,
            seatNo: Number.isFinite(s) && s > 0 ? s : null,
          }
        })
        .filter((x) => x.submissionId)
      this.busy = true
      this.okMsg = ''
      this.errorMsg = ''
      try {
        const res = await saveAdminActivitySeatingAssignments(this.activityId, items)
        const warns = (res && res.warnings) || []
        this.okMsg = `已保存 ${res && res.saved != null ? res.saved : 0} 条` + (warns.length ? `；${warns.join('；')}` : '')
        await this.load()
      } catch (e) {
        this.errorMsg = e && e.message ? e.message : '保存座位失败'
      } finally {
        this.busy = false
      }
    },
    pickSeatingImport() {
      const el = this.$refs.seatingImportInput
      if (el) el.click()
    },
    addHoldRow() {
      this.holdKeySeq += 1
      this.holdRows.push({
        id: null,
        _key: `new-${this.holdKeySeq}`,
        _tableNo: '',
        _seatNo: '',
        _label: '',
        _note: '',
      })
    },
    async removeHoldRow(idx, row) {
      if (row && row.id && this.activityId) {
        try {
          await deleteAdminActivitySeatingHold(this.activityId, row.id)
        } catch (e) {
          this.errorMsg = (e && e.message) || '删除占座失败'
          return
        }
      }
      this.holdRows.splice(idx, 1)
    },
    async saveHolds() {
      if (!this.activityId) return
      const items = (this.holdRows || [])
        .map((h) => {
          const t = Number(h._tableNo)
          const s = Number(h._seatNo)
          if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(s) || s <= 0) {
            return h.id ? { id: h.id, tableNo: null, seatNo: null } : null
          }
          return {
            id: h.id || null,
            tableNo: t,
            seatNo: s,
            label: h._label || '',
            note: h._note || '',
          }
        })
        .filter(Boolean)
      this.busy = true
      this.okMsg = ''
      this.errorMsg = ''
      try {
        const res = await saveAdminActivitySeatingHolds(this.activityId, items)
        const warns = (res && res.warnings) || []
        this.okMsg = `占座已保存 ${res && res.saved != null ? res.saved : 0} 条` + (warns.length ? `；${warns.join('；')}` : '')
        await this.load()
      } catch (e) {
        this.errorMsg = (e && e.message) || '保存占座失败'
      } finally {
        this.busy = false
      }
    },
    async onSeatingImportFile(ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0]
      if (ev && ev.target) ev.target.value = ''
      if (!file || !this.activityId) return
      this.busy = true
      this.okMsg = ''
      this.errorMsg = ''
      try {
        const res = await importAdminActivitySeatingAssignments(this.activityId, file)
        const warns = (res && res.warnings) || []
        this.okMsg = `导入完成，已保存 ${res && res.saved != null ? res.saved : 0} 条` + (warns.length ? `；${warns.join('；')}` : '')
        await this.load()
      } catch (e) {
        this.errorMsg = e && e.message ? e.message : '导入排座失败'
      } finally {
        this.busy = false
      }
    },
    async publishPlan() {
      if (!this.activityId) return
      if (!await adminConfirm('确认发布？发布后小程序将显示座位，须全员已分配。')) return
      this.busy = true
      this.okMsg = ''
      this.errorMsg = ''
      try {
        const res = await publishAdminActivitySeatingPlan(this.activityId)
        this.okMsg = (res && res.message) || '已发布'
        await this.load()
      } catch (e) {
        this.errorMsg = e && e.message ? e.message : '发布失败'
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  max-width: 100%;
  box-sizing: border-box;
}
.hidden-file {
  display: none;
}
.page-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}
.back-btn:hover {
  transform: translateX(-1px);
}
.back-btn-icon {
  width: 32px;
  height: 32px;
  display: block;
}
h2 {
  margin: 0;
  font-size: 18px;
  color: #101828;
}
.sub-line {
  margin: 0 0 10px 48px;
  font-size: 14px;
  color: #667085;
}
.tip {
  background: #f5f8ff;
  border: 1px solid #dae5ff;
  color: #2a4b8d;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  line-height: 1.55;
  font-size: 13px;
}
.error {
  color: #d92d20;
  font-size: 13px;
  margin: 0 0 8px;
}
.ok {
  color: #067647;
  font-size: 13px;
  margin: 0 0 8px;
}
.plan-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-bottom: 14px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 10px;
}
.plan-bar-label {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
}
.plan-stat {
  font-size: 13px;
  color: #475467;
}
.plan-stat strong {
  color: #101828;
}
.muted-inline {
  color: #98a2b3;
  font-size: 13px;
}
.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.tool-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.tool-label {
  font-weight: 600;
  color: #344054;
  font-size: 13px;
  white-space: nowrap;
}
.tool-input {
  height: 38px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  color: #1d2939;
  background: #fff;
  box-sizing: border-box;
}
.tool-input--num {
  width: 88px;
}
.table-wrap {
  overflow-x: auto;
}
table {
  width: 100%;
  border-collapse: collapse;
  min-width: 960px;
}
th,
td {
  border-bottom: 1px solid #edf0f5;
  padding: 8px 10px;
  font-size: 13px;
  text-align: left;
  vertical-align: middle;
}
thead th {
  background: #f9fafb;
  color: #344054;
  font-weight: 700;
  white-space: nowrap;
}
.col-seq {
  width: 56px;
  text-align: center;
  white-space: nowrap;
}
.col-center {
  text-align: center;
}
.td-wrap {
  max-width: 180px;
  word-break: break-word;
}
.td-sub {
  max-width: 160px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}
.td-nowrap {
  white-space: nowrap;
}
.cell-input {
  width: 72px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
  box-sizing: border-box;
}
.hold-wrap {
  margin-bottom: 16px;
}
.sub-h {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
}
.seat-preview {
  font-weight: 600;
  color: #175cd3;
  white-space: nowrap;
}
.muted {
  color: #98a2b3;
  font-size: 13px;
  margin-top: 8px;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
  height: 36px;
  line-height: 1;
}
.btn + .btn {
  margin-left: 0;
}
.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.pill {
  display: inline-block;
  min-width: 58px;
  text-align: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-blue {
  color: #175cd3;
  background: #eff8ff;
  border: 1px solid #b2ddff;
}
</style>
